import React, { useState, useEffect, useCallback } from 'react';
import { showToast } from '@/components/ui/Toast';
import { useTranslation } from 'react-i18next';
import { TabBar } from '@/components/ui/TabBar';
import { db } from '@/firebase';
import { useAuth } from '@/context/AuthContext';
import {
  collection,
  addDoc,
  serverTimestamp,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  doc,
  getDoc,
  getDocs,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Bug,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Check,
  Lock,
  BarChart3,
  Send,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import {
  SubscriptionTier,
  FEATURE_REGISTRY,
  type FeatureDef,
  type BugSeverity,
  type FeaturePoll,
  type UserFeedback,
} from '@everyday-os/shared';

// Helper for dates
const formatDate = (timestamp: any) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

export function FeedbackAndPolls() {
  const { profile } = useAuth();
  const { t } = useTranslation();
  const userId = profile?.id;
  const userSubscriptionTier = profile?.subscription?.tier || SubscriptionTier.FREE;

  // --- Tab State ---
  const [activeTab, setActiveTab] = useState<'bug' | 'suggestion' | 'polls'>('bug');

  // --- Bug Form State ---
  const [bugTitle, setBugTitle] = useState('');
  const [bugArea, setBugArea] = useState('');
  const [bugSeverity, setBugSeverity] = useState<BugSeverity>('medium');
  const [bugDescription, setBugDescription] = useState('');
  const [bugSubmitting, setBugSubmitting] = useState(false);
  const [bugSuccess, setBugSuccess] = useState(false);

  // --- Suggestion Form State ---
  const [sugTitle, setSugTitle] = useState('');
  const [sugArea, setSugArea] = useState('');
  const [sugDescription, setSugDescription] = useState('');
  const [sugSubmitting, setSugSubmitting] = useState(false);
  const [sugSuccess, setSugSuccess] = useState(false);

  // --- Polls State ---
  const [polls, setPolls] = useState<FeaturePoll[]>([]);
  const [userVotes, setUserVotes] = useState<Map<string, string>>(new Map());
  const [pollsLoading, setPollsLoading] = useState(false);

  // --- My Submissions State ---
  const [showSubmissions, setShowSubmissions] = useState(false);
  const [submissions, setSubmissions] = useState<UserFeedback[]>([]);
  const [subsLoading, setSubsLoading] = useState(false);

  // --- Handlers ---

  const handleBugSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !profile?.email) return;
    setBugSubmitting(true);

    try {
      await addDoc(collection(db, 'userFeedback'), {
        type: 'bug',
        title: bugTitle.trim(),
        area: bugArea || undefined,
        severity: bugSeverity,
        description: bugDescription.trim(),
        submittedBy: userId,
        submitterEmail: profile.email,
        submitterTier: userSubscriptionTier,
        submittedAt: serverTimestamp(),
        userAgent: navigator.userAgent,
        appVersion: '1.20',
        status: 'new',
      });
      setBugSuccess(true);
      // Reset handled by "Submit another" button
    } catch (error) {
      console.error('Error submitting bug:', error);
      showToast('Failed to submit bug report. Please try again.', 'error');
    } finally {
      setBugSubmitting(false);
    }
  };

  const handleSuggestionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !profile?.email) return;
    setSugSubmitting(true);

    try {
      await addDoc(collection(db, 'userFeedback'), {
        type: 'suggestion',
        title: sugTitle.trim(),
        area: sugArea || undefined,
        description: sugDescription.trim(),
        submittedBy: userId,
        submitterEmail: profile.email,
        submitterTier: userSubscriptionTier,
        submittedAt: serverTimestamp(),
        userAgent: navigator.userAgent,
        appVersion: '1.20',
        status: 'new',
      });
      setSugSuccess(true);
    } catch (error) {
      console.error('Error submitting suggestion:', error);
      showToast('Failed to submit suggestion. Please try again.', 'error');
    } finally {
      setSugSubmitting(false);
    }
  };

  const resetBugForm = () => {
    setBugTitle('');
    setBugArea('');
    setBugSeverity('medium');
    setBugDescription('');
    setBugSuccess(false);
  };

  const resetSugForm = () => {
    setSugTitle('');
    setSugArea('');
    setSugDescription('');
    setSugSuccess(false);
  };

  // --- Polls Effect ---
  useEffect(() => {
    if (activeTab !== 'polls') return;
    setPollsLoading(true);

    const q = query(
      collection(db, 'featurePolls'),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const pollsData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as FeaturePoll[];
      setPolls(pollsData);
      setPollsLoading(false);

      if (userId) {
        // Fetch votes for current user
        const votePromises = pollsData.map((poll) =>
          getDoc(doc(db, 'featurePolls', poll.id, 'votes', userId))
        );
        const voteSnaps = await Promise.all(votePromises);
        const newVotes = new Map<string, string>();
        voteSnaps.forEach((snap, index) => {
          if (snap.exists()) {
            newVotes.set(pollsData[index].id, snap.data().optionId);
          }
        });
        setUserVotes(newVotes);
      }
    });

    return () => unsubscribe();
  }, [activeTab, userId]);

  const handleVote = async (pollId: string, optionId: string) => {
    if (!userId) return;
    const poll = polls.find((p) => p.id === pollId);
    if (!poll) return;

    // Tier check inside handler (though UI should prevent this)
    const tierRank: Record<string, number> = { FREE: 0, BASIC: 1, PREMIUM: 2 };
    if (tierRank[userSubscriptionTier] < tierRank[poll.minTierToVote]) {
      // The UI will handle showing the "Upgrade" prompt
      return;
    }

    const prevOptionId = userVotes.get(pollId);

    try {
      const fn = httpsCallable(getFunctions(), 'apiPolls');
      await fn({ action: 'vote', pollId, optionId });

      // Update local state optimistically
      setUserVotes((prev) => {
        const next = new Map(prev);
        if (prevOptionId === optionId) next.delete(pollId);
        else next.set(pollId, optionId);
        return next;
      });
    } catch (error) {
      console.error('Vote failed:', error);
      showToast('Failed to register vote. Please try again.', 'error');
    }
  };

  // --- My Submissions Effect ---
  useEffect(() => {
    if (!showSubmissions || !userId) return;
    setSubsLoading(true);

    const fetchSubmissions = async () => {
      try {
        const q = query(
          collection(db, 'userFeedback'),
          where('submittedBy', '==', userId),
          orderBy('submittedAt', 'desc'),
          limit(20)
        );
        const snap = await getDocs(q);
        setSubmissions(snap.docs.map((d) => ({ id: d.id, ...d.data() } as UserFeedback)));
      } catch (error) {
        console.error('Error fetching submissions:', error);
      } finally {
        setSubsLoading(false);
      }
    };

    fetchSubmissions();
  }, [showSubmissions, userId]);

  // --- Render Helpers ---

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      new: 'bg-slate-200 text-slate-700',
      in_review: 'bg-blue-100 text-blue-700',
      planned: 'bg-purple-100 text-purple-700',
      resolved: 'bg-green-100 text-green-700',
      closed: 'bg-slate-100 text-slate-500',
    };
    const style = styles[status] || styles.new;
    return (
      <span className={`text-2xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${style}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const tierRank: Record<string, number> = { FREE: 0, BASIC: 1, PREMIUM: 2 };

  return (
    <div className="max-w-page-settings mx-auto p-4 md:p-6 space-y-8">
      {/* Header */}
      <div className="text-center space-y-2">
        <p className="text-slate-500 dark:text-slate-400">{t('feedback.tagline', 'Help shape the future of EverydayOS')}</p>
      </div>

      {/* Tabs */}
      <TabBar
        tabs={[
          { id: 'bug',        label: t('feedback.tab_bug', 'Report Bug') },
          { id: 'suggestion', label: t('feedback.tab_suggest', 'Suggest') },
          { id: 'polls',      label: t('feedback.tab_polls', 'Polls') },
        ]}
        activeTab={activeTab}
        onChange={id => setActiveTab(id as 'bug' | 'suggestion' | 'polls')}
      />

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* --- BUG TAB --- */}
        {activeTab === 'bug' && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
            {bugSuccess ? (
              <div className="bg-green-50 border border-green-100 rounded-2xl p-8 text-center space-y-4">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto">
                  <Check size={32} />
                </div>
                <h3 className="text-xl font-bold text-green-800">{t('feedback.bug_success_title', 'Thanks!')}</h3>
                <p className="text-green-700">{t('feedback.bug_success_body', 'Your bug report was submitted.')}</p>
                <button
                  onClick={resetBugForm}
                  className="bg-green-600 text-white px-6 py-2 rounded-full font-bold text-sm hover:bg-green-700 transition-colors"
                >
                  {t('feedback.submit_another', 'Submit another')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleBugSubmit} className="bg-white dark:bg-slate-800 rounded-2xl p-6 md:p-8 shadow-sm border border-slate-100 dark:border-slate-700 space-y-6">
                <div>
                  <label className="text-2xs font-black uppercase tracking-wider text-slate-500 block mb-2">
                    {t('feedback.label_title', 'Title')}
                  </label>
                  <input
                    type="text"
                    required
                    maxLength={100}
                    value={bugTitle}
                    onChange={(e) => setBugTitle(e.target.value)}
                    placeholder={t('feedback.placeholder_bug_title', 'Brief summary of the issue')}
                    className="w-full rounded-xl border border-slate-200 dark:border-slate-700 p-3 focus:ring-2 focus:ring-brand outline-none bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white"
                  />
                </div>

                <div>
                  <label className="text-2xs font-black uppercase tracking-wider text-slate-500 block mb-2">
                    {t('feedback.label_area', 'Area')}
                  </label>
                  <select
                    value={bugArea}
                    onChange={(e) => setBugArea(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 dark:border-slate-700 p-3 focus:ring-2 focus:ring-brand outline-none bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white"
                  >
                    <option value="">{t('feedback.select_area', 'Select area (optional)')}</option>
                    {FEATURE_REGISTRY.map((f: FeatureDef) => (
                      <option key={f.id} value={f.label}>
                        {f.label}
                      </option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="text-2xs font-black uppercase tracking-wider text-slate-500 block mb-2">
                    {t('feedback.label_severity', 'Severity')}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {(['low', 'medium', 'high', 'critical'] as BugSeverity[]).map((sev) => (
                      <button
                        key={sev}
                        type="button"
                        onClick={() => setBugSeverity(sev)}
                        className={`px-4 py-2 rounded-full text-xs font-bold uppercase transition-all ${
                          bugSeverity === sev
                            ? 'bg-brand text-white shadow-lg'
                            : 'border border-slate-200 text-slate-600 hover:border-brand hover:text-brand'
                        }`}
                      >
                        {sev}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-2xs font-black uppercase tracking-wider text-slate-500">
                      {t('feedback.label_description', 'Description')}
                    </label>
                    <span className="text-2xs text-slate-400">{bugDescription.length}/500</span>
                  </div>
                  <textarea
                    required
                    maxLength={500}
                    value={bugDescription}
                    onChange={(e) => setBugDescription(e.target.value)}
                    placeholder={t('feedback.placeholder_bug_desc', 'Steps to reproduce...')}
                    className="w-full h-32 rounded-xl border border-slate-200 dark:border-slate-700 p-3 focus:ring-2 focus:ring-brand outline-none bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={bugSubmitting}
                  className="w-full bg-brand text-white rounded-full font-black uppercase tracking-widest text-xs px-6 py-3 hover:bg-[#4a72d9] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {bugSubmitting ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                  {t('feedback.submit_report', 'Submit Report')}
                </button>
              </form>
            )}
          </div>
        )}

        {/* --- SUGGESTION TAB --- */}
        {activeTab === 'suggestion' && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
            {sugSuccess ? (
              <div className="bg-green-50 border border-green-100 rounded-2xl p-8 text-center space-y-4">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto">
                  <Check size={32} />
                </div>
                <h3 className="text-xl font-bold text-green-800">{t('feedback.suggestion_success_title', 'Suggestion Sent!')}</h3>
                <p className="text-green-700">{t('feedback.suggestion_success_body', 'Thank you for helping us improve.')}</p>
                <button
                  onClick={resetSugForm}
                  className="bg-green-600 text-white px-6 py-2 rounded-full font-bold text-sm hover:bg-green-700 transition-colors"
                >
                  {t('feedback.suggest_another', 'Suggest another')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSuggestionSubmit} className="bg-white dark:bg-slate-800 rounded-2xl p-6 md:p-8 shadow-sm border border-slate-100 dark:border-slate-700 space-y-6">
                <div>
                  <label className="text-2xs font-black uppercase tracking-wider text-slate-500 block mb-2">
                    {t('feedback.suggestion_title', 'Feature Title')}
                  </label>
                  <input
                    type="text"
                    required
                    maxLength={100}
                    value={sugTitle}
                    onChange={(e) => setSugTitle(e.target.value)}
                    placeholder={t('feedback.placeholder_sug_title', 'What would you like to see?')}
                    className="w-full rounded-xl border border-slate-200 dark:border-slate-700 p-3 focus:ring-2 focus:ring-brand outline-none bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white"
                  />
                </div>

                <div>
                  <label className="text-2xs font-black uppercase tracking-wider text-slate-500 block mb-2">
                    {t('feedback.area_optional', 'Area (Optional)')}
                  </label>
                  <select
                    value={sugArea}
                    onChange={(e) => setSugArea(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 dark:border-slate-700 p-3 focus:ring-2 focus:ring-brand outline-none bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white"
                  >
                    <option value="">{t('feedback.select_area', 'Select area (optional)')}</option>
                    {FEATURE_REGISTRY.map((f: FeatureDef) => (
                      <option key={f.id} value={f.label}>
                        {f.label}
                      </option>
                    ))}
                    <option value="Something new">Something new</option>
                  </select>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-2xs font-black uppercase tracking-wider text-slate-500">
                      {t('feedback.why_helpful', 'Why it would help')}
                    </label>
                    <span className="text-2xs text-slate-400">{sugDescription.length}/500</span>
                  </div>
                  <textarea
                    required
                    maxLength={500}
                    value={sugDescription}
                    onChange={(e) => setSugDescription(e.target.value)}
                    placeholder={t('feedback.placeholder_sug_desc', 'Describe the value this would bring...')}
                    className="w-full h-32 rounded-xl border border-slate-200 dark:border-slate-700 p-3 focus:ring-2 focus:ring-brand outline-none bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={sugSubmitting}
                  className="w-full bg-brand text-white rounded-full font-black uppercase tracking-widest text-xs px-6 py-3 hover:bg-[#4a72d9] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sugSubmitting ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                  {t('feedback.submit_suggestion', 'Submit Suggestion')}
                </button>
              </form>
            )}
          </div>
        )}

        {/* --- POLLS TAB --- */}
        {activeTab === 'polls' && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
            {pollsLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="animate-spin text-brand" size={32} />
              </div>
            ) : polls.length === 0 ? (
              <div className="text-center py-12 text-slate-500 bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700">
                <BarChart3 className="mx-auto mb-2 opacity-50" size={32} />
                <p>{t('feedback.no_polls', 'No active polls at the moment.')}</p>
              </div>
            ) : (
              polls.map((poll) => {
                const userVote = userVotes.get(poll.id);
                const totalVotes = (poll.options ?? []).reduce((acc, opt) => acc + (opt.votes || 0), 0);
                const canVote = tierRank[userSubscriptionTier] >= tierRank[poll.minTierToVote];

                return (
                  <div key={poll.id} className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-100 dark:border-slate-700">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-lg font-bold text-slate-800 dark:text-white">{poll.title}</h3>
                      {poll.minTierToVote !== SubscriptionTier.FREE && (
                        <span className="text-2xs font-bold px-2 py-1 bg-amber-100 text-amber-700 rounded-md uppercase">
                          {poll.minTierToVote} only
                        </span>
                      )}
                    </div>
                    {poll.description && <p className="text-sm text-slate-500 mb-6">{poll.description}</p>}

                    <div className="space-y-3">
                      {(poll.options ?? []).map((option) => {
                        const percent = totalVotes > 0 ? ((option.votes || 0) / totalVotes) * 100 : 0;
                        const isVoted = userVote === option.id;
                        const isOtherVoted = userVote && userVote !== option.id;
                        
                        return (
                          <div key={option.id} className="relative group">
                            {/* Progress Bar Background */}
                            <div className="absolute inset-0 bg-slate-100 dark:bg-slate-700 rounded-xl overflow-hidden">
                              <div 
                                className="h-full bg-brand/20 transition-all duration-500"
                                style={{ width: `${percent}%` }}
                              />
                            </div>
                            
                            {/* Content */}
                            <div className="relative flex items-center justify-between p-3 pl-4">
                              <span className="font-medium text-slate-800 dark:text-white z-10 flex items-center gap-2">
                                {option.text}
                                {userSubscriptionTier === SubscriptionTier.PREMIUM && isVoted && (
                                  <span className="text-2xs font-bold bg-brand text-white px-1.5 rounded">×2</span>
                                )}
                              </span>
                              
                              <div className="flex items-center gap-3 z-10">
                                <span className="text-xs font-bold text-slate-500">{Math.round(percent)}%</span>
                                
                                {canVote ? (
                                  <button
                                    onClick={() => handleVote(poll.id, option.id)}
                                    disabled={!userId}
                                    className={`text-xs font-bold px-3 py-1.5 rounded-full transition-all ${
                                      isVoted
                                        ? 'bg-brand text-white shadow-md'
                                        : isOtherVoted
                                        ? 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50'
                                        : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-brand hover:text-brand'
                                    }`}
                                  >
                                    {isVoted ? t('feedback.voted', 'Voted ✓') : isOtherVoted ? t('feedback.change_vote', 'Change Vote') : t('feedback.vote', 'Vote')}
                                  </button>
                                ) : (
                                  <button
                                    disabled
                                    className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full bg-slate-100 text-slate-400 cursor-not-allowed"
                                  >
                                    <Lock size={10} /> {t('feedback.upgrade_lock', 'Upgrade')}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-4 text-xs text-slate-400 text-right">
                      {totalVotes} vote{totalVotes !== 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* My Submissions */}
      <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
        <button
          onClick={() => setShowSubmissions(!showSubmissions)}
          className="w-full flex items-center justify-between text-left group"
        >
          <h2 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
            {t('feedback.my_submissions', 'My Submissions')}
            {showSubmissions ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </h2>
        </button>

        {showSubmissions && (
          <div className="mt-4 space-y-3 animate-in slide-in-from-top-2 duration-200">
            {subsLoading ? (
              <div className="py-8 flex justify-center">
                <Loader2 className="animate-spin text-slate-400" size={24} />
              </div>
            ) : submissions.length === 0 ? (
              <p className="text-center text-slate-500 py-8 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                {t('feedback.no_submissions', "You haven't submitted any feedback yet.")}
              </p>
            ) : (
              submissions.map((sub) => (
                <div key={sub.id} className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-100 dark:border-slate-700 flex items-start gap-4 shadow-sm">
                  <div className={`p-2 rounded-lg ${sub.type === 'bug' ? 'bg-rose-100 text-rose-500' : 'bg-blue-100 text-blue-500'}`}>
                    {sub.type === 'bug' ? <Bug size={20} /> : <MessageSquare size={20} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-bold text-slate-800 dark:text-white truncate pr-2">{sub.title}</h4>
                      {getStatusBadge(sub.status)}
                    </div>
                    <div className="text-xs text-slate-400">
                      {t('feedback.submitted', 'Submitted {{date}}', { date: formatDate(sub.submittedAt) })}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}